import $ from 'jquery';

import loadScript from 'chairisher/util/script';

import { getRecaptchaSiteKey } from 'chairisher/context/auth';
import { getRecaptchaJSUrl } from 'chairisher/context/script';
import { handleAsyncError } from 'chairisher/util/logging';

let loadingPromise;

/**
 * Loads google's recaptcha library.
 *
 * @return {Promise<object>}
 */
function loadRecaptcha() {
    if (!loadingPromise) {
        loadingPromise = loadScript({ scriptName: 'grecaptcha', scriptSrc: getRecaptchaJSUrl() });
    }

    return loadingPromise;
}

/**
 * Executes recaptcha v3: gets a unique token from google and returns a deferred with the token and action name as
 * data. When formEl is provided, writes the token into that form via name selectors. Otherwise falls back to the
 * legacy global #id_recaptcha_token input. Does not rewrite recaptcha_action; that value is set when the form loads.
 *
 * @param {string} actionName a string to identify what action the user took
 * @param {HTMLElement|jQuery=} formEl optional form to embed the token into
 * @returns {jQuery.Deferred}
 */
export default function executeRecaptcha(actionName, formEl = null) {
    const deferred = $.Deferred();
    const siteKey = getRecaptchaSiteKey();
    if (siteKey) {
        loadRecaptcha().then((recaptcha) => {
            recaptcha.ready(() => {
                recaptcha.execute(siteKey, { action: actionName }).then((token) => {
                    let recaptchaData = {};
                    if (token) {
                        if (formEl) {
                            $(formEl).find('[name=recaptcha_token]').val(token);
                        } else {
                            // embed token in form and send to backend to verify on form submission
                            $('#id_recaptcha_token').val(token);
                        }
                        recaptchaData = {
                            recaptcha_action: actionName,
                            recaptcha_token: token,
                        };
                    }
                    deferred.resolve(recaptchaData);
                }, handleAsyncError);
            });
        }, handleAsyncError);
    } else {
        deferred.resolve();
    }
    return deferred;
}

/**
 * Runs reCAPTCHA for the form's loaded action and writes only the token into that form.
 * Aborts when recaptcha_action is missing so Google is not called with a blank action.
 *
 * @param {HTMLElement|jQuery} formEl
 * @returns {Promise<object|undefined>}
 * @throws {Error} When the form has no recaptcha_action
 */
export async function executeRecaptchaForForm(formEl) {
    const actionName = $(formEl).find('[name=recaptcha_action]').val();
    if (!actionName) {
        const error = new Error('Missing recaptcha_action on form submit');
        handleAsyncError(error);
        throw error;
    }
    return executeRecaptcha(actionName, formEl);
}
